/**
 * V4CanvasView.jsx — Prove7 Control Vector's clean-slate canvas renderer.
 *
 * The strangler-fig replacement for the 16,713-line CanvasPageV3 host shell.
 * V4 deliberately reuses the proven pieces of the V3 stack:
 *   - `flatToElkHierarchical` (the G479-G483 working-baseline layout pipeline)
 *   - `elkToReactFlow` (ELK → ReactFlow node/edge conversion)
 *   - `V2_NODE_TYPES` (the rendered node components — switch / fork / loop
 *     containers, sentinels, leaves, terminate pills, sub-workflows)
 *   - `V2Edge` (custom edge component with branch-label rendering)
 *
 * What V4 does differently from V3:
 *   - Patches A/B/C from Round 1 are baked in NATIVELY (drag persistence,
 *     manual edge drawing with rejection toast, orphan-drop messaging).
 *   - No legacy hidden SVG canvas tree mounting per render.
 *   - No five overlapping lock booleans — one `readOnly` prop.
 *   - No `validateAndRepairDAG` silently mutating user graphs (the host
 *     decides whether to repair, and surfaces the repair report to the user).
 *
 * Phase scope progression:
 *   Phase 1 (this file's current state): minimal render + drag persistence
 *       + manual edge draw + select. Reuses V2 layout + node + edge code.
 *   Phase 2: per-branch + on case/leg/loop-body containers (Patch D done
 *       natively), delete, save/load round-trip.
 *   Phase 3: undo/redo command stack, copy/paste, multi-select with viz.
 *   Phase 4: beta polish + cutover.
 *
 * Phase 1 contract:
 *   Props:
 *     payload      { nodes, connections }   the canvas graph to render
 *     onConnect    ({ sourceNodeId, targetNodeId, sourceHandle, targetHandle }) => void
 *                                                     manual edge create (Patch B)
 *     onSelectNode (sourceNodeId | null) => void      single-click select
 *     onPaneClick  () => void                          background click
 *     readOnly     boolean                             gates all mutations
 *
 *     Node repositioning was REMOVED 2026-05-29 — ELK owns positions,
 *     Re-layout button is the only re-position path. See the
 *     handleNodeDragStop / nodesDraggable comment blocks below.
 *
 * Host responsibilities (kept OUT of this component, lives in CanvasPageV4):
 *   - Workflow IO (services/api load/save)
 *   - URL routing (?id=)
 *   - Property panel
 *   - Picker, palette, toolbar
 *   - Toast surface
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  // Phase 6c-1 — MiniMap. @xyflow/react ships it inline; severity-tinted
  // dots make problem nodes spottable from afar on big workflows.
  MiniMap,
  useNodesState,
  useEdgesState,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import ELK from 'elkjs/lib/elk.bundled.js';
import { flatToElkHierarchical } from '../../utils/v2/flatToElkHierarchical';
import { elkToReactFlow } from '../../utils/v2/elkToReactFlow';
import { V2_NODE_TYPES } from '../v2/nodes/V2Nodes';
import V2Edge from '../v2/edges/V2Edge';
import { V2_STONE } from '../v2/nodes/v2Palette';
// User correction (post-Phase 7g): manual_start / manual_end render
// as flat text pills in V4, not the 72×72 V2 circles. V4Sentinel
// overrides only the v2Sentinel key — every other v2* node stays put.
import V4Sentinel from './V4Sentinel';

const V4_EDGE_TYPES = { v2Edge: V2Edge };

// Override only the sentinel key. The spread preserves every other v2*
// node component so containers / leaves / forks / loops render unchanged.
const V4_NODE_TYPES = { ...V2_NODE_TYPES, v2Sentinel: V4Sentinel };

let _elk = null;
function getElk() {
  if (!_elk) _elk = new ELK();
  return _elk;
}

/**
 * Run the V3-proven layout pipeline: flat canvas → ELK hierarchical IR
 * → ReactFlow nodes/edges. Errors are returned as `{ error }` so the
 * host can surface them as toasts rather than blanking the canvas.
 */
async function layoutHierarchical(payload) {
  const nodes = Array.isArray(payload?.nodes) ? payload.nodes : [];
  const connections = Array.isArray(payload?.connections) ? payload.connections : [];
  if (nodes.length === 0) return { nodes: [], edges: [], error: null };
  try {
    const elkInput = flatToElkHierarchical({ nodes, connections });
    const elk = getElk();
    const elkLaid = await elk.layout(elkInput);
    // elkToReactFlow returns { rfNodes, rfEdges } (the V2 surface contract).
    // Map to {nodes, edges} for our outer interface so callers don't have
    // to know about the rfNodes/rfEdges name.
    const { rfNodes, rfEdges } = elkToReactFlow(elkLaid);
    return { nodes: rfNodes, edges: rfEdges, error: null };
  } catch (e) {
    return { nodes: [], edges: [], error: e?.message || String(e) };
  }
}

// Phase 2a — leaf-class tasks get the per-node "+" decoration.
// Sentinels (Start/End) and terminate pills are intentionally excluded —
// Start has its own entry path, End is terminal, terminate is a leaf-style
// end state.
const LEAF_DECORATION_TYPES = new Set(['v2Leaf', 'v2Stub']);

// Phase 2b — case + leg containers get the per-BRANCH "+" decoration.
// V2CaseContainer / V2LegContainer read data.onAddAfter and render a "+"
// when present. The callback fires with `{kind:'branch', ownerNodeId,
// branchName, ...}` — the host resolves that to a branchEdgeId by walking
// canvas.connections (the switch/fork's outgoing edge for this branch).
const BRANCH_DECORATION_TYPES = new Set(['v2CaseContainer', 'v2LegContainer']);

function V4CanvasViewInner({
  payload,
  // Node repositioning removed 2026-05-29 (was: onNodeMove).
  // See header comment + handleNodeDragStop block below.
  onConnect,
  onSelectNode,
  // Phase 7h — double-click on a node. The host inspects the node
  // type and decides what to open (e.g. cognitive_loop → Cognitive
  // Brief slideout). zoomOnDoubleClick stays false so this never
  // races with React Flow's built-in zoom. Signature:
  //   (sourceNodeId: string, nodeType: string) => void
  onNodeDoubleClick,
  onPaneClick,
  // Phase 2a — fires when the user clicks "+" on a task card. The host
  // opens the TaskPicker with a target derived from `sourceNodeId`.
  // Signature: (sourceNodeId: string) => void
  onAddAfter,
  // Phase 2a — fires on Delete/Backspace when a node is selected. The
  // host calls canvas.deleteNode(selectedNodeId). Wired here (not in the
  // host) so the keyboard listener is scoped to the canvas surface.
  onDeleteSelected,
  // Phase 2a — single source of truth for selected node id. Used to
  // decorate the selected node with a visible highlight ring (closing
  // V3's "⌘A is silent" regression) and to gate the keyboard delete.
  selectedNodeId,
  // Phase 3a — undo/redo. ⌘Z / ⌘⇧Z (or Ctrl+Z / Ctrl+Y).
  onUndo,
  onRedo,
  // Phase 3b — copy/paste. ⌘C / ⌘V (or Ctrl+C / Ctrl+V).
  onCopy,
  onPaste,
  // Phase 4a — multi-delete. Called when Delete/Backspace fires and the
  // user has multiple nodes selected via shift+click or box-select. The
  // host receives the array of canvas-shape sourceNodeIds (translated
  // from ReactFlow's rfNode ids by V4CanvasView). Falls back to the
  // single-select onDeleteSelected when only one node is selected (or
  // none from RF) — preserves Phase 2a semantics.
  onDeleteMany,
  // Drag-and-drop palette-to-canvas was REMOVED 2026-05-29. It free-
  // placed nodes at the cursor's flow-coords, bypassing the smart
  // cascade routing every other add path uses (palette click → after-
  // selected → before-end → free with toast; per-node + → TaskPicker;
  // ⌘K → cascade). Result: a never-ending stream of orphan / disconnected
  // nodes. Operator quote: "this drag-drop is creating all kind of
  // issues." The fix: remove the entire drag path. Click paths handle
  // every legitimate add case. Don't reintroduce without a written
  // smart-routing strategy that prevents the orphan class.
  // (Old prop name: onDropNode — searchable on purpose.)
  // Phase 5e-3 — per-node validation issues. Map: sourceNodeId → highest
  // severity string ('error' | 'warning' | 'info'). Drives the per-node
  // colored outline + corner badge so operators see at-a-glance which
  // nodes have problems. Empty map = clean graph, no decoration.
  validationByNodeId,
  // Phase 6c-5 — Smart re-layout. Host bumps this number to force an
  // ELK re-run when the operator clicks "Re-layout" — useful after a
  // session of manual drags has misaligned the canonical positions.
  // The next ELK pass re-derives positions from the IR and overwrites
  // any user drags (which were always session-scoped per V4CanvasView
  // line 130 — never persisted to the BE).
  relayoutKey = 0,
  readOnly = false,
}) {
  const [rfNodes, setRfNodes, onNodesChange] = useNodesState([]);
  const [rfEdges, setRfEdges, onEdgesChange] = useEdgesState([]);
  const [layoutError, setLayoutError] = useState(null);
  const wrapperRef = useRef(null);
  // (Drag-drop receivers removed 2026-05-29. The isDragOver state,
  // useReactFlow instance for screenToFlowPosition, and onDragOver /
  // onDragLeave / onDrop handlers all went with them. If a future
  // surface needs the RF instance for something else, re-import
  // useReactFlow from @xyflow/react — it's still in the V4_NODE_TYPES
  // hosting tree.)
  // Phase 4a — track RF's multi-selected rfNodes so Delete can remove all
  // of them in one shot. Ref-based (not state) so updates don't trigger
  // re-renders; the keyboard handler reads the current value at fire time.
  const selectedRfNodesRef = useRef([]);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  // Re-layout when payload changes. This is the single source of truth
  // for what gets rendered — host pushes payload, we lay it out, RF renders.
  // Phase 2a — decorate leaf nodes with `data.onAddAfter` so V2Leaf's "+"
  // button renders. Decorate the selected node with `data.selected` so a
  // future render tier can paint a highlight ring (V3's ⌘A is silent —
  // we close that here). Decoration is a separate pass after layout so
  // the layout pipeline doesn't see callback references in its input.
  useEffect(() => {
    let cancelled = false;
    setLayoutError(null);
    layoutHierarchical(payload).then(({ nodes, edges, error }) => {
      if (cancelled || !mountedRef.current) return;
      if (error) {
        setLayoutError(error);
        return;
      }
      let decorated = nodes;
      if (typeof onAddAfter === 'function' && !readOnly) {
        decorated = decorated.map((n) => {
          // Leaf nodes — onAddAfter receives the sourceNodeId string
          // (V2Leaf wraps it before invoking). Phase 2a semantics.
          if (LEAF_DECORATION_TYPES.has(n.type)) {
            return { ...n, data: { ...n.data, onAddAfter } };
          }
          // Branch containers — onAddAfter receives the branch target
          // object directly (V2CaseContainer / V2LegContainer wrap with
          // `{kind:'branch', ownerNodeId, branchName, ...}`). Phase 2b.
          if (BRANCH_DECORATION_TYPES.has(n.type)) {
            return { ...n, data: { ...n.data, onAddAfter } };
          }
          return n;
        });
      }
      // Phase 3c — visual highlight for the selected node. Paint a 3px
      // teal outline via inline rfNode.style.outline (V4-only — V3 doesn't
      // run this decoration). outlineOffset:4 gives the ring breathing
      // room around the node chrome. The flag also goes into data.selected
      // for any future component that wants to react.
      if (selectedNodeId) {
        decorated = decorated.map((n) => {
          const srcId = (n.data && n.data.sourceNodeId) || n.id;
          if (srcId !== selectedNodeId) return n;
          return {
            ...n,
            data: { ...n.data, selected: true },
            selected: true,
            style: {
              ...(n.style || {}),
              outline: '3px solid #14b8a6',
              outlineOffset: 4,
              borderRadius: 8,
            },
          };
        });
      }
      // Phase 5e-3 — validation outlines.
      // Each rfNode whose sourceNodeId appears in validationByNodeId gets
      // a colored ring (red=error, amber=warning, blue=info). Layered
      // AFTER selection so the validation color "wins" for highlighted
      // nodes too — a selected error-node still reads as an error.
      // The teal selection ring is preserved via outlineOffset:6 (one px
      // further out than the validation ring at 4) — this keeps both
      // visible together as a double-ring effect on selected-AND-invalid.
      if (validationByNodeId && validationByNodeId.size > 0) {
        const SEVERITY_COLOR = {
          error:   '#dc2626',
          warning: '#d97706',
          info:    '#2563eb',
        };
        decorated = decorated.map((n) => {
          const srcId = (n.data && n.data.sourceNodeId) || n.id;
          const severity = validationByNodeId.get(srcId);
          if (!severity) return n;
          const color = SEVERITY_COLOR[severity] || SEVERITY_COLOR.warning;
          const isSelected = !!(n.data && n.data.selected);
          return {
            ...n,
            data: { ...n.data, validationSeverity: severity },
            style: {
              ...(n.style || {}),
              outline: `2px solid ${color}`,
              outlineOffset: isSelected ? 8 : 4,
              borderRadius: 8,
              /* Subtle box-shadow tint so the severity reads even at
               * zoom-out levels where outline width compresses. */
              boxShadow: severity === 'error'
                ? '0 0 0 6px rgba(220, 38, 38, 0.08)'
                : severity === 'warning'
                  ? '0 0 0 6px rgba(217, 119, 6, 0.08)'
                  : '0 0 0 6px rgba(37, 99, 235, 0.06)',
            },
          };
        });
      }
      setRfNodes(decorated);
      setRfEdges(edges);
    });
    return () => { cancelled = true; };
  }, [payload, onAddAfter, readOnly, selectedNodeId, setRfNodes, setRfEdges, validationByNodeId, relayoutKey]);

  // Phase 2a + 3a + 3b — keyboard shortcuts on the canvas surface.
  // Single listener covers Delete, ⌘Z, ⌘⇧Z, ⌘C, ⌘V — ordering matters
  // because some keys (Delete) only fire when a node is selected, whereas
  // undo/redo fire whenever the canvas is focused. Input-field guard
  // applies to ALL shortcuts so typing in the picker search never nukes
  // a node OR triggers an unintended undo.
  useEffect(() => {
    if (readOnly) return undefined;
    const onKey = (e) => {
      const t = e.target;
      const tag = t?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || t?.isContentEditable) return;
      const mod = e.metaKey || e.ctrlKey;

      // Delete / Backspace — Phase 2a (single) + 4a (multi)
      if ((e.key === 'Delete' || e.key === 'Backspace') && !mod) {
        // Multi-delete path takes precedence when RF reports ≥2 selected.
        const multi = selectedRfNodesRef.current || [];
        if (multi.length >= 2 && typeof onDeleteMany === 'function') {
          const sourceIds = multi
            .map((n) => (n?.data && n.data.sourceNodeId) || n?.id)
            .filter(Boolean);
          if (sourceIds.length > 0) {
            e.preventDefault();
            onDeleteMany(sourceIds);
            return;
          }
        }
        // Single-select fallback — preserves Phase 2a semantics.
        if (typeof onDeleteSelected !== 'function' || !selectedNodeId) return;
        e.preventDefault();
        onDeleteSelected();
        return;
      }

      // Undo / Redo — Phase 3a
      // ⌘Z (no shift) → undo
      // ⌘⇧Z OR Ctrl+Y → redo (both common conventions)
      if (mod && (e.key === 'z' || e.key === 'Z') && !e.shiftKey) {
        if (typeof onUndo !== 'function') return;
        e.preventDefault();
        onUndo();
        return;
      }
      if (mod && ((e.key === 'z' || e.key === 'Z') && e.shiftKey || e.key === 'y' || e.key === 'Y')) {
        if (typeof onRedo !== 'function') return;
        e.preventDefault();
        onRedo();
        return;
      }

      // Copy / Paste — Phase 3b
      // ⌘C only fires when a node is selected (matches OS convention of
      // "copy something specific"); ⌘V is always available so a user can
      // paste into an empty area without a current selection.
      if (mod && (e.key === 'c' || e.key === 'C')) {
        if (typeof onCopy !== 'function' || !selectedNodeId) return;
        e.preventDefault();
        onCopy();
        return;
      }
      if (mod && (e.key === 'v' || e.key === 'V')) {
        if (typeof onPaste !== 'function') return;
        e.preventDefault();
        onPaste();
        return;
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [readOnly, onDeleteSelected, onDeleteMany, onUndo, onRedo, onCopy, onPaste, selectedNodeId]);

  // ── handleNodeDragStop was REMOVED 2026-05-29 ──
  // Originally Round-1 Patch A: persist node drag back to the host so
  // the canvas remembered manual positions across reloads. Operator
  // reported it never worked properly — the persisted positions
  // fought ELK's re-layout, sometimes snapped back, sometimes drifted,
  // sometimes detached nodes from their cluster. ELK now owns layout
  // exclusively; the Re-layout button (Phase 6c-5) is the only user-
  // controlled re-position path. nodesDraggable below is held off so
  // ReactFlow won't even initiate the drag.

  // ── Patch B native: manual edge creation ──
  const handleConnect = useCallback((connection) => {
    if (readOnly || typeof onConnect !== 'function') return;
    if (!connection?.source || !connection?.target) return;
    const srcRf = rfNodes.find((n) => n.id === connection.source);
    const tgtRf = rfNodes.find((n) => n.id === connection.target);
    const sourceNodeId = (srcRf?.data && srcRf.data.sourceNodeId) || connection.source;
    const targetNodeId = (tgtRf?.data && tgtRf.data.sourceNodeId) || connection.target;
    if (!sourceNodeId || !targetNodeId || sourceNodeId === targetNodeId) return;
    onConnect({
      sourceNodeId,
      targetNodeId,
      sourceHandle: connection.sourceHandle || null,
      targetHandle: connection.targetHandle || null,
    });
  }, [readOnly, onConnect, rfNodes]);

  const handleNodeClick = useCallback((_event, rfNode) => {
    if (typeof onSelectNode !== 'function') return;
    const sourceNodeId = (rfNode?.data && rfNode.data.sourceNodeId) || rfNode?.id;
    onSelectNode(sourceNodeId || null);
  }, [onSelectNode]);

  // Phase 7h — double-click. Host receives (sourceNodeId, canvasType)
  // so it can branch on type without re-walking the canvas. canvasType
  // is the ORIGINAL canvas node type (e.g. 'cognitive_loop'), not the
  // React Flow render type (e.g. 'v2Leaf'). The flatToElkHierarchical
  // pipeline parks the original type on `data.canvasType`.
  const handleNodeDoubleClick = useCallback((_event, rfNode) => {
    if (typeof onNodeDoubleClick !== 'function') return;
    const sourceNodeId = (rfNode?.data && rfNode.data.sourceNodeId) || rfNode?.id;
    const canvasType = rfNode?.data?.canvasType || rfNode?.type || null;
    if (sourceNodeId) onNodeDoubleClick(sourceNodeId, canvasType);
  }, [onNodeDoubleClick]);

  const handlePaneClick = useCallback(() => {
    if (typeof onSelectNode === 'function') onSelectNode(null);
    if (typeof onPaneClick === 'function') onPaneClick();
  }, [onSelectNode, onPaneClick]);

  // ── Drag-drop palette receivers were REMOVED 2026-05-29 ─────────
  // (handleDragOver / handleDragLeave / handleDrop). The drop targeted
  // wrapper props (onDragOver / onDragLeave / onDrop / outline) below
  // were stripped at the same time. See the comment block at the
  // onDropNode-prop site (top of this component) for the rationale.

  const reactFlowProps = useMemo(() => ({
    nodes: rfNodes,
    edges: rfEdges,
    onNodesChange,
    onEdgesChange,
    onNodeClick: handleNodeClick,
    onNodeDoubleClick: handleNodeDoubleClick,
    onPaneClick: handlePaneClick,
    /* onNodeDragStop was wired here pre-2026-05-29 (Round-1 Patch A).
     * Removed with handleNodeDragStop + onNodeMove — see comment above. */
    onConnect: handleConnect,
    nodeTypes: V4_NODE_TYPES,
    edgeTypes: V4_EDGE_TYPES,
    fitView: true,
    fitViewOptions: { padding: 0.2, maxZoom: 1, minZoom: 0.1 },
    minZoom: 0.1,
    maxZoom: 2.5,
    proOptions: { hideAttribution: true },
    defaultEdgeOptions: { style: { strokeWidth: 1.5, stroke: '#475569' } },
    zoomOnDoubleClick: false,
    /* Node-drag held permanently off (2026-05-29). Repositioning never
     * worked properly — manual positions fought ELK re-layout and
     * routinely drifted / orphaned nodes. Click-to-select stays, edge
     * draw stays, only ReactFlow's built-in node move is disabled. */
    nodesDraggable: false,
    nodesConnectable: !readOnly,
    edgesUpdatable: !readOnly,
    // Phase 3c — multi-select. Shift+click adds a node to the selection
    // (ReactFlow's default modifier). selectionOnDrag enables box-select
    // on shift-drag of the empty pane. Default selection style comes from
    // ReactFlow's own `.react-flow__node.selected` class — we don't need
    // to add anything beyond enabling these props.
    multiSelectionKeyCode: ['Shift', 'Meta'],
    selectionOnDrag: !readOnly,
    selectNodesOnDrag: false,
    // Phase 4a — record RF's selection so Delete can hit multi-select.
    // ReactFlow fires this on every selection change (single click, shift-
    // click, box-select, pane click). We stash the selected rfNodes in a
    // ref; the keyboard handler reads it at fire time.
    onSelectionChange: ({ nodes: selNodes }) => {
      selectedRfNodesRef.current = Array.isArray(selNodes) ? selNodes : [];
    },
  }), [
    rfNodes, rfEdges, onNodesChange, onEdgesChange,
    handleNodeClick, handleNodeDoubleClick, handlePaneClick, handleConnect,
    readOnly,
  ]);

  return (
    <div
      ref={wrapperRef}
      style={{
        position: 'relative', width: '100%', height: '100%',
        background: '#f8fafc',
      }}
    >
      {layoutError && (
        <div style={{
          position: 'absolute', top: 12, right: 12, zIndex: 10,
          padding: '8px 12px', fontSize: 12, color: '#9f1239',
          background: '#fef2f2', border: '1px solid #fecdd3', borderRadius: 8,
        }}>
          Layout error: {layoutError}
        </div>
      )}
      <ReactFlow {...reactFlowProps}>
        <Background color={V2_STONE[200]} gap={24} />
        <Controls
          showInteractive={false}
          position="bottom-center"
          orientation="horizontal"
          style={{ display: 'flex', flexDirection: 'row', gap: 4 }}
        />
        {/* Phase 6c-1 + 7c-5 — MiniMap. Moved to bottom-LEFT in 7c-5
         * (was bottom-right; conflicted with the AI chip and right rail).
         * Node dots are tinted by validation severity using the same map
         * that decorates the main canvas, so problem nodes are spottable
         * from the overview without needing to scroll back. Sentinels
         * (start/end) get a neutral teal so they remain identifiable
         * regardless of validation state. All other unflagged nodes
         * use the stone grey so the canvas isn't visually noisy. */}
        <MiniMap
          position="bottom-left"
          pannable
          zoomable
          ariaLabel="Workflow mini-map"
          maskColor="rgba(15, 23, 42, 0.04)"
          style={{
            width: 180,
            height: 120,
            background: '#ffffff',
            border: `0.5px solid ${V2_STONE[200]}`,
            borderRadius: 6,
          }}
          nodeColor={(n) => {
            const srcId = (n.data && n.data.sourceNodeId) || n.id;
            const severity = validationByNodeId && validationByNodeId.get(srcId);
            if (severity === 'error')   return '#dc2626';
            if (severity === 'warning') return '#d97706';
            if (severity === 'info')    return '#2563eb';
            // Sentinels — distinct teal so the spine endpoints are
            // visually anchored on the overview. The flatToElkHierarchical
            // pipeline exposes the original canvas type on data.canvasType
            // (see flatToElkHierarchical.js:290).
            const t = n.data && n.data.canvasType;
            if (t === 'manual_start' || t === 'start') return '#0d9488';
            if (t === 'end' || t === 'terminate')      return '#475569';
            // Container chrome (switch/fork containers) shouldn't dominate
            // the mini-map — fade them so leaf nodes are the focus.
            if (n.data && n.data.isContainer)          return 'rgba(148, 163, 184, 0.25)';
            return V2_STONE[400];
          }}
          nodeStrokeWidth={0}
          nodeBorderRadius={3}
        />
      </ReactFlow>
    </div>
  );
}

/**
 * V4CanvasView — host-facing renderer.
 *
 * Wraps the inner view in ReactFlowProvider so consumers (CanvasPageV4)
 * can use ReactFlow hooks elsewhere if needed.
 */
export default function V4CanvasView(props) {
  return (
    <ReactFlowProvider>
      <V4CanvasViewInner {...props} />
    </ReactFlowProvider>
  );
}
